import { Button, CheckIcon, FormNavigation, FormStepper } from "@fla/components";
import classNames from "classnames";
import { GetStartedLayout } from "components";
// import withGetStarted from "components/HOCs/withGetStarted";
import { useGetStarted } from "hooks";
import { FirebaseRefs, GetStartedStoreTypes, SliceStatus } from "interfaces";
import { selectUserCreatedInSession, selectUserProfile } from "pages/User";
import RegisterInFlow from "pages/User/Register/RegisterInFlow";
import RegisterModal from "pages/User/Register/RegisterModal";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Oval } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FirestoreService } from "services";
import { getRedirectUrl } from "utils";

import UserDetailsModal from "./UserDetailsModal";
import { selectInitialDetails } from "./getStartedSelectors";
import { getStartedActions } from "./getStartedSlice";
import { initialState, stepperSteps } from "./helper";
import { formSteps } from "./steps";

function GetStarted() {
  const user = useSelector(selectUserProfile);
  const quoteInitialDetails = useSelector(selectInitialDetails);
  const userCreatedInSession = useSelector(selectUserCreatedInSession);

  const dispatch = useDispatch();
  const navigation = useNavigate();
  const {
    save,
    finish
    // onEndChat
  } = useGetStarted();

  const [formState, setFormState] = useState(initialState);
  const [currentStep, setCurrentStep] = useState(1);

  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [submitStatus, setSubmitStatus] = useState(SliceStatus.Idle);
  const [loadingStatus, setLoadingStatus] = useState(SliceStatus.Idle);

  // to check whether a document is already being saved in the remote DB
  const [hasInitDetDoc, setHasInitDetDoc] = useState(false);

  //
  const [enquiryStatus, setEnquiryStatus] = useState(SliceStatus.Idle);
  const [enquiryData, setEnquiryData] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  }>({
    first_name: "",
    last_name: "",
    email: user?.email || "",
    phone: ""
  });
  //
  // const [chatEnded, setChatEnded] = useState(false);

  const isLastStep = () => currentStep === Object.keys(formSteps).length;
  const isFirstStep = () => currentStep === 1;

  const isLast = isLastStep();
  const isFirst = isFirstStep();

  const CurrentForm = formSteps[String(currentStep) as keyof typeof formSteps];

  const [open, setOpen] = useState(false);

  const onClose = () => {
    setOpen(false);

    if (enquiryStatus === SliceStatus.Resolved) {
      finish(formState);
      navigation("/");
    }
  };

  const onOpen = () => setOpen(true);

  const handleBackClick = () => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const onSubmit = async (formData: Partial<GetStartedStoreTypes>) => {
    try {
      setSubmitStatus(SliceStatus.Pending);
      setFormState(prev => ({ ...prev, ...formData }));
      const payload = { data: { ...formState, ...formData }, currentStep, docExists: hasInitDetDoc, setHasInitDetDoc };
      await save(payload);

      if (isFirst) {
        setCurrentStep(prev => prev + 1);
        if (!completedSteps.includes(currentStep)) setCompletedSteps(prev => [...prev, currentStep]);
      } else if (!isLast) {
        setCurrentStep(prev => prev + 1);
        if (!completedSteps.includes(currentStep)) setCompletedSteps(prev => [...prev, currentStep]);
      } else {
        onOpen();
      }
      setSubmitStatus(SliceStatus.Resolved);
    } catch (error) {
      console.error("ERROR!", error);
      setSubmitStatus(SliceStatus.Rejected);
    }
  };

  const onChangeStep = (val: number) => {
    setCurrentStep(val);
  };

  const formId = "get-started-form";
  const isSubmitting = submitStatus === SliceStatus.Pending;

  const userDetailsModalProps = {
    open,
    onClose,
    userId: user?.uid,
    setEnquiryStatus,
    store: formState,
    defaultValues: enquiryData
  };

  useEffect(() => {
    if (user?.uid && !userCreatedInSession) {
      (async () => {
        try {
          setLoadingStatus(SliceStatus.Pending);
          const doc = await FirestoreService.getDocument({ coll: FirebaseRefs.INITIAL_DETAILS, docId: user?.uid });

          if (doc.exists) {
            if (doc?.data?.data) {
              setFormState(doc?.data?.data as any);
            }

            if (doc.data?.step) {
              setCurrentStep(doc.data?.step);
              setCompletedSteps(
                Array(doc.data?.step)
                  .fill(0)
                  .map((_, i) => i + 1)
              );
            }

            // if (doc?.data?.chatEnded) {
            //   setChatEnded(true);
            // }

            if (doc?.data?.user) {
              setEnquiryData(doc?.data?.user as any);
            }

            dispatch(
              getStartedActions.setInitialDetails({
                data: doc?.data?.data as any,
                step: doc?.data?.step,
                isCompleted: doc?.data?.isCompleted ?? false
              })
            );
            setHasInitDetDoc(true);
          } else {
            setHasInitDetDoc(false);
          }

          setLoadingStatus(SliceStatus.Resolved);
        } catch (error) {
          setLoadingStatus(SliceStatus.Rejected);
        }
      })();
    } else if (!user?.uid) {
      setCurrentStep(1);
      setHasInitDetDoc(false);
      setFormState(initialState);
    }
  }, [user, userCreatedInSession]);

  useEffect(() => {
    const section = document.querySelector("#formContainer");
    if (section && typeof section.scrollIntoView === "function") {
      if (currentStep === 3) {
        section.scrollIntoView({ behavior: "smooth", block: "end" });
      } else {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [currentStep]);

  if (quoteInitialDetails.isCompleted) {
    return (
      <GetStartedLayout bgColor="bg-gray-light" lgColumns="grid-cols-1">
        <article className="w-full h-[calc(100vh-80px)] flex flex-col justify-center">
          <div className="max-w-sm lg:max-w-3xl space-y-5 mx-auto p-5 md:p-10 rounded-lg -mt-20 flex flex-col items-center">
            <CheckIcon strokeColor="text-green-600" classes="w-12 h-12" />
            <h2 className="font-bold text-center capitalize">We got your details</h2>
            <p className="text-center">Our legal team will call you to get started with the next steps shortly.</p>
            <Button
              bgColor="bg-primary"
              className="w-full py-2.5 mt-10 rounded-lg tracking-wider hover:bg-primary-faded"
              onClick={() => window.open(getRedirectUrl().website, "_blank")}
            >
              Go To Website
            </Button>
          </div>
        </article>
      </GetStartedLayout>
    );
  }

  if (loadingStatus === SliceStatus.Pending) {
    return (
      <GetStartedLayout bgColor="bg-gray-light" lgColumns="grid-cols-1">
        <article className="w-full h-full flex flex-col justify-center items-center">
          <Oval height={50} width={50} strokeWidth={5} color="#0696EB" secondaryColor="#ccc" />;
        </article>
      </GetStartedLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>SplitWise | Get Started - Quote</title>
      </Helmet>
      <GetStartedLayout bgColor="bg-gray-light" lgColumns="grid-cols-1">
        <article className="space-y-5 px-3 lg:px-0" id="formContainer">
          <div>
            <FormStepper
              steps={stepperSteps}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onChangeStep={onChangeStep}
              activeColor="primary"
              classes="max-w-2xl mx-auto h-fit lg:h-full mt-5 lg:mt-0"
              orientation="horizontal"
            />
          </div>

          {currentStep > 2 && !user ? (
            <RegisterInFlow />
          ) : (
            <div
              className={classNames("bg-white max-w-2xl mx-auto rounded-lg md:shadow-md", {
                "px-5 md:p-10 py-5": [1, 2].includes(currentStep)
              })}
            >
              <CurrentForm
                formId={formId}
                store={formState}
                onSubmit={onSubmit}
                // chatEnded={chatEnded}
              />

              {/* {currentStep === 3 && !chatEnded ? (
              <button
                className="block mx-auto mt-10 text-primary italic font-semibold hover:underline underline-offset-4"
                onClick={() => {
                  onEndChat(() => setChatEnded(true));
                }}
              >
                CLICK WHEN FINISHED CHATTING
              </button>
            ) : currentStep === 3 && chatEnded ? (
              <div className="flex flex-col items-center py-5 space-y-3">
                <CheckIcon strokeColor="text-green-600" classes="w-12 h-12" />
                <p className="font-semibold text-xl">All Questions Answered</p>
                <p>Click “Next” to continue</p>
              </div>
            ) : null} */}
              <div
                className={classNames({
                  "px-5 pb-5": [3, 4].includes(currentStep)
                })}
              >
                <FormNavigation
                  form=""
                  isFirst={isFirst}
                  isLast={isLast}
                  handleBackClick={handleBackClick}
                  isSubmitting={isSubmitting}
                  // hideNextBtn={currentStep === 3 && !chatEnded}
                  customLastButton={
                    <Button
                      form={formId}
                      type="submit"
                      bgColor="bg-primary"
                      className="w-32 max-w-xs h-10 rounded-lg tracking-wider hover:bg-primary-faded"
                      isSubmitting={isSubmitting}
                    >
                      {isLast ? "Get Started" : "NEXT"}
                    </Button>
                  }
                />
              </div>
            </div>
          )}
          {!isLast && !user ? <RegisterModal /> : null}

          <UserDetailsModal {...userDetailsModalProps} />
        </article>
      </GetStartedLayout>
    </>
  );
}

export default GetStarted;
